<script setup lang="ts">
import type { OrganizationJs } from "@/generated/models";
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import { SimpleDialogContent, SimpleDialogDescription } from "ls/components/simple-dialog";
import PurchaseCfbAuthorSeatsSection from "ls/features/organizations/SettingsOrganizationUpgradePlan/PurchaseCfbAuthorSeatsSection.vue";
import { toOrganizationBilling } from "ls/router/builders";
import { DialogClose, DialogTitle } from "reka-ui";
import { useAccessLevelFromOrganization } from "../../organizations/useOrganizationAccessLevel";
import { useOrganizationCloudPlusStatus } from "../../organizations/useOrganizationCloudPlusStatus";
import CloudForBusinessWhitelistMessage from "./CloudForBusinessWhitelistMessage.vue";

const props = defineProps<{
  organization: OrganizationJs;
  isOnBillingPage?: boolean;
}>();

const { isAdmin } = useAccessLevelFromOrganization(() => props.organization);
const { isWhitelisted } = useOrganizationCloudPlusStatus(() => props.organization);
</script>

<template>
  <SimpleDialogContent variant="x-large" background-color="grey">
    <SimpleDialogDescription class="sr-only">
      Add Cloud Author seats for unlimited documents and whiteboards
    </SimpleDialogDescription>
    <div class="flex justify-between gap-6">
      <div class="flex-1">
        <div class="flex h-full flex-col justify-between">
          <div class="mb-4 flex bg-slate-100 text-slate-500">
            <div class="flex flex-col gap-2 rounded-sm py-4 text-slate-500">
              <div class="text-lg/6 font-semibold text-slate-800">
                <DialogTitle v-if="!isOnBillingPage">
                  Add Cloud Author seats for unlimited documents and whiteboards
                </DialogTitle>
                <div v-else>
                  Add Cloud Author seats for unlimited documents and whiteboards
                </div>
              </div>
              <CloudForBusinessWhitelistMessage v-if="isWhitelisted" :organization="props.organization" />
              <div v-else>
                <p class="mb-2 text-sm text-slate-500">
                  Create and collaborate on unlimited documents and whiteboards across your organization.
                </p>
                <p class="text-sm text-slate-500">
                  Contact <SimpleButton href="mailto:support@axure.com" size="auto" variant="link">
                    support@axure.com
                  </SimpleButton> for a trial.
                </p>
              </div>
            </div>
          </div>
          <div>
            <p v-if="isAdmin" class="my-0 text-xs text-slate-500">
              Go to
              <DialogClose>
                <SimpleButton variant="link" size="auto" :href="toOrganizationBilling(props.organization)" class="h-3">
                  billing
                </SimpleButton>
              </DialogClose>
              to manage subscriptions.
            </p>
            <p class="my-0 text-xs text-slate-500">
              Questions?
              <SimpleButton
                variant="link"
                size="auto"
                target="_blank"
                href="https://www.axure.com/contact-us"
                class="h-3"
              >
                Contact us
              </SimpleButton>
            </p>
          </div>
        </div>
      </div>
      <div class="max-w-64">
        <PurchaseCfbAuthorSeatsSection :organization="props.organization" />
      </div>
    </div>
  </SimpleDialogContent>
</template>
